import React, { useState, useEffect, useCallback } from 'react';
import { X, Users, RefreshCw, ChevronUp, ChevronDown } from 'lucide-react';
import { listDeploymentRecipientHistory } from '../utils/cloudApi';

/**
 * DeploymentRecipientsModal
 * Shows a popup table of recipients who participated in a deployed survey,
 * with status badges and timestamps. Triggered from Recipients → Show Recipients.
 */
export default function DeploymentRecipientsModal({ isOpen, onClose, deploymentId, surveyName }) {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [sortBy, setSortBy] = useState('created_at');
    const [sortOrder, setSortOrder] = useState('DESC');

    const load = useCallback(async () => {
        if (!deploymentId) return;
        setLoading(true);
        setError('');
        try {
            const result = await listDeploymentRecipientHistory(deploymentId);
            setRows(result.data || []);
        } catch (e) {
            setError(e.message || 'Failed to load recipient history');
        } finally {
            setLoading(false);
        }
    }, [deploymentId]);

    useEffect(() => {
        if (isOpen && deploymentId) load();
    }, [isOpen, deploymentId, load]);

    if (!isOpen) return null;

    // Client-side sort
    const sorted = [...rows].sort((a, b) => {
        const av = a[sortBy] ?? '';
        const bv = b[sortBy] ?? '';
        const cmp = typeof av === 'string' ? av.localeCompare(bv) : (av > bv ? 1 : av < bv ? -1 : 0);
        return sortOrder === 'ASC' ? cmp : -cmp;
    });

    const toggleSort = (col) => {
        if (sortBy === col) {
            setSortOrder(o => o === 'ASC' ? 'DESC' : 'ASC');
        } else {
            setSortBy(col);
            setSortOrder('ASC');
        }
    };

    const sortArrow = (col) => {
        if (sortBy !== col) return null;
        return sortOrder === 'ASC'
            ? <ChevronUp size={14} style={{ verticalAlign: 'middle', marginLeft: 2 }} />
            : <ChevronDown size={14} style={{ verticalAlign: 'middle', marginLeft: 2 }} />;
    };

    const fmtTime = (ts) => {
        if (!ts) return '—';
        const d = new Date(ts);
        if (isNaN(d.getTime())) return '—';
        const month = d.toLocaleString(undefined, { month: 'short' });
        const day = d.getDate();
        const h24 = d.getHours();
        const h12 = (h24 % 12) || 12;
        const min = String(d.getMinutes()).padStart(2, '0');
        const ampm = h24 >= 12 ? 'PM' : 'AM';
        return `${month} ${day} ${h12}:${min}${ampm}`;
    };

    const fmtDuration = (secs) => {
        if (secs == null) return '—';
        if (secs < 60) return `${secs}s`;
        const m = Math.floor(secs / 60);
        const s = secs % 60;
        return s ? `${m}m ${s}s` : `${m}m`;
    };

    const statusColors = {
        completed: { bg: '#E8F5E9', color: '#2E7D32', border: '#81C784' },
        started: { bg: '#E3F2FD', color: '#1565C0', border: '#90CAF9' },
        abandoned: { bg: '#FFF3E0', color: '#E65100', border: '#FFB74D' },
        sent: { bg: '#F3E5F5', color: '#7B1FA2', border: '#CE93D8' },
        invited: { bg: '#F3E5F5', color: '#7B1FA2', border: '#CE93D8' },
    };

    return (
        <div style={styles.overlay} onClick={onClose}>
            <div style={styles.modal} onClick={e => e.stopPropagation()}>
                {/* Header */}
                <div style={styles.header}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <h2 style={{ margin: 0, fontSize: 18, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 8 }}>
                            <Users size={22} /> Deployment Recipients
                        </h2>
                        <span style={styles.badge}>{rows.length} recipient{rows.length !== 1 ? 's' : ''}</span>
                    </div>
                    <button onClick={onClose} style={styles.closeBtn} title="Close"><X size={20} /></button>
                </div>

                {/* Survey name subtitle */}
                {surveyName && (
                    <div style={{ padding: '4px 20px 8px', fontSize: 13, color: '#5F6368', borderBottom: '1px solid #E8EAED' }}>
                        {surveyName}
                    </div>
                )}

                {/* Error banner */}
                {error && (
                    <div style={{ padding: '8px 20px', background: '#FFEBEE', borderBottom: '1px solid #FFCDD2', color: '#C62828', fontSize: 13 }}>
                        {error}
                        <button onClick={() => setError('')} style={{ float: 'right', background: 'none', border: 'none', cursor: 'pointer', color: '#C62828', display: 'inline-flex', alignItems: 'center' }}>
                            <X size={16} />
                        </button>
                    </div>
                )}

                {/* Toolbar */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 20px', borderBottom: '1px solid #E8EAED' }}>
                    <button onClick={load} disabled={loading} style={{ ...styles.actionBtn, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                        <RefreshCw size={14} /> Refresh
                    </button>
                    {loading && <span style={{ fontSize: 13, color: '#5F6368' }}>Loading…</span>}
                </div>

                {/* Table */}
                <div style={{ flex: 1, overflow: 'auto', padding: '0 20px 16px' }}>
                    <table style={styles.table}>
                        <thead>
                            <tr>
                                <th style={styles.th} onClick={() => toggleSort('name')}>
                                    Name{sortArrow('name')}
                                </th>
                                <th style={styles.th} onClick={() => toggleSort('email')}>
                                    Email{sortArrow('email')}
                                </th>
                                <th style={{ ...styles.th, textAlign: 'center' }} onClick={() => toggleSort('completion_status')}>
                                    Status{sortArrow('completion_status')}
                                </th>
                                <th style={styles.th} onClick={() => toggleSort('channel_used')}>
                                    Channel{sortArrow('channel_used')}
                                </th>
                                <th style={styles.th} onClick={() => toggleSort('started_at')}>
                                    Started{sortArrow('started_at')}
                                </th>
                                <th style={styles.th} onClick={() => toggleSort('completion_timestamp')}>
                                    Completed{sortArrow('completion_timestamp')}
                                </th>
                                <th style={{ ...styles.th, textAlign: 'center' }}>
                                    Progress
                                </th>
                                <th style={{ ...styles.th, textAlign: 'right' }} onClick={() => toggleSort('time_to_complete_seconds')}>
                                    Duration{sortArrow('time_to_complete_seconds')}
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {sorted.length === 0 && !loading && (
                                <tr><td colSpan={8} style={{ padding: 24, textAlign: 'center', color: '#9CA3AF', fontSize: 14 }}>
                                    No recipient history for this deployment
                                </td></tr>
                            )}
                            {sorted.map((r, i) => {
                                const sc = statusColors[r.completion_status] || { bg: '#F5F5F5', color: '#616161', border: '#BDBDBD' };
                                const progress = r.questions_total > 0
                                    ? `${r.questions_answered}/${r.questions_total}`
                                    : '—';
                                return (
                                    <tr key={r.history_id || i} style={{ borderBottom: '1px solid #F1F3F5' }}>
                                        <td style={styles.td}>{r.name || '—'}</td>
                                        <td style={{ ...styles.td, fontSize: 12, color: '#5F6368' }}>{r.email || '—'}</td>
                                        <td style={{ ...styles.td, textAlign: 'center' }}>
                                            <span style={{
                                                display: 'inline-block',
                                                padding: '2px 10px',
                                                borderRadius: 12,
                                                fontSize: 11,
                                                fontWeight: 500,
                                                background: sc.bg,
                                                color: sc.color,
                                                border: `1px solid ${sc.border}`,
                                            }}>
                                                {r.completion_status || '—'}
                                            </span>
                                        </td>
                                        <td style={{ ...styles.td, fontSize: 12 }}>{r.channel_used || '—'}</td>
                                        <td style={{ ...styles.td, fontSize: 12 }}>{fmtTime(r.started_at)}</td>
                                        <td style={{ ...styles.td, fontSize: 12 }}>{fmtTime(r.completion_timestamp)}</td>
                                        <td style={{ ...styles.td, textAlign: 'center', fontSize: 12 }}>{progress}</td>
                                        <td style={{ ...styles.td, textAlign: 'right', fontSize: 12 }}>{fmtDuration(r.time_to_complete_seconds)}</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}

const styles = {
    overlay: {
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999,
    },
    modal: {
        background: '#fff', borderRadius: 12, width: '90vw', maxWidth: 1000,
        maxHeight: '80vh', display: 'flex', flexDirection: 'column',
        boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
    },
    header: {
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '16px 20px', borderBottom: '1px solid #E8EAED',
    },
    badge: {
        background: '#E8F0FE', color: '#1967D2', padding: '2px 10px',
        borderRadius: 12, fontSize: 12, fontWeight: 500,
    },
    closeBtn: {
        background: 'none', border: 'none', cursor: 'pointer',
        fontSize: 18, color: '#5F6368', padding: 4,
        display: 'inline-flex', alignItems: 'center',
    },
    actionBtn: {
        padding: '6px 14px', border: '1px solid #DADCE0', borderRadius: 6,
        background: '#fff', cursor: 'pointer', fontSize: 13, color: '#3C4043',
    },
    table: {
        width: '100%', borderCollapse: 'collapse', marginTop: 12,
    },
    th: {
        textAlign: 'left', padding: '8px 10px', fontSize: 12,
        fontWeight: 600, color: '#5F6368', borderBottom: '2px solid #E8EAED',
        cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap',
    },
    td: {
        padding: '8px 10px', fontSize: 13, color: '#202124',
    },
};
